import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
    Box,
    IconButton,
    Drawer,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    Button,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ptBR, enUS } from 'date-fns/locale';
import { journalService } from '../../services/JournalService';
import { JournalEntry } from '../../types/journal';
import theme from '../../theme';
import { fullScreenButtonStyle } from '../../utils/buttonStyles';

export const JournalEntryPage = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [entry, setEntry] = useState<Partial<JournalEntry>>({
        title: '',
        content: '',
        mood: 'neutral',
        tags: []
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();
    // This should eventually come from props or store
    const startTime = new Date().toLocaleTimeString(i18next.language === 'pt-BR' ? 'pt-BR' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    // Load existing entry if editing
    useEffect(() => {
        const loadEntry = async () => {
            if (id && id !== 'new') {
                try {
                    const data = await journalService.getEntry(id);
                    setEntry(data);
                } catch (error) {
                    console.error('Failed to load entry:', error);
                    navigate('/journal');
                }
            }
        };
        loadEntry();
    }, [id, navigate]);

    // Save draft to localStorage whenever content changes
    useEffect(() => {
        localStorage.setItem('journalDraft', entry.content || '');
    }, [entry.content]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (id && id !== 'new') {
                await journalService.updateEntry(id, entry);
            } else {
                await journalService.createEntry({
                    ...entry,
                    createdAt: new Date(),
                } as Omit<JournalEntry, 'id'>);
            }
            navigate('/journal');
        } catch (error) {
            console.error('Failed to save entry:', error);
            // Add error handling UI here
        }
    };

    const currentDate = new Date().toLocaleDateString(i18next.language === 'pt-BR' ? 'pt-BR' : 'en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const moodOptions = [
        { key: 'happy', label: t('journal.moods.happy') },
        { key: 'calm', label: t('journal.moods.calm') },
        { key: 'anxious', label: t('journal.moods.anxious') },
        { key: 'stressed', label: t('journal.moods.stressed') },
        { key: 'excited', label: t('journal.moods.excited') },
        { key: 'neutral', label: t('journal.moods.neutral') },
        { key: 'sad', label: t('journal.moods.sad') },
        { key: 'angry', label: t('journal.moods.angry') },
    ];

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                maxWidth: 600,
                mx: 'auto'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={() => navigate('/journal')} size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ ml: 2 }}>{currentDate}</Typography>
                </Box>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: '80px' }}>
                    <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                        <RadioGroup
                            row
                            value={entry.mood}
                            onChange={(e) => setEntry({ ...entry, mood: e.target.value })}
                            sx={{
                                '& .MuiFormControlLabel-root': {
                                    margin: 1,
                                    '& .Mui-checked': {
                                        '& + .MuiFormControlLabel-label': {
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText,
                                            borderRadius: 1
                                        }
                                    }
                                },
                                '& .MuiFormControlLabel-label': {
                                    padding: '4px 8px',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                        borderRadius: 1
                                    }
                                },
                                '& .MuiRadio-root': {
                                    display: 'none'
                                }
                            }}
                        >
                            {moodOptions.map((mood) => (
                                <FormControlLabel
                                    key={mood.key}
                                    value={mood.key}
                                    control={<Radio />}
                                    label={mood.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    <textarea
                        value={entry.content}
                        onChange={(e) => setEntry({ ...entry, content: e.target.value })}
                        placeholder={t('journal.writeYourThoughts')}
                        style={{
                            width: '100%',
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '8px',
                            fontSize: '1.1rem',
                            backgroundColor: 'transparent',
                            color: theme.palette.text.primary
                        }}
                        autoFocus
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                            ...fullScreenButtonStyle,
                            position: 'fixed',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            maxWidth: 552, // 600px maxWidth - 48px padding (24px each side)
                            width: 'calc(100% - 48px)'
                        }}
                    >
                        {id && id !== 'new' ? t('journal.update') : t('journal.save')} {t('journal.myDay')}
                    </Button>
                </form>


            </Box>

        </ThemeProvider>
    );
};

export default JournalEntryPage;
